import type { FC, KeyboardEvent, PointerEvent, ReactNode } from 'react'
import type { SongTrack } from '@/types'
import type { PlayableSource } from '@/lib/audio/engine'

import { useMemo, useRef } from 'react'
import { cn } from '@/lib/utils'
import { band } from '@/constants/portfolio'
import { formatTime } from '@/lib/format'
import { audioEngine } from '@/lib/audio/engine'
import { usePlayback } from '@/hooks/use-playback'
import LevelBars from '@/components/landing/level-bars'
import PlayGlyph from '@/components/landing/play-glyph'

interface TrackRowProps {
    track: SongTrack
    index: number
    visible: boolean
}

const SEEK_STEP = 5

const trackSource = (track: SongTrack): PlayableSource => ({
    id: `track-${track.id}`,
    url: track.previewAudioUrl,
    synth: track.synth,
    durationHint: track.durationSeconds
})

// One song in the slanted tracklist: play disc, seekable progress, level bars and every state
// a preview can be in (idle, loading, playing, paused, blocked by the browser, unavailable).
const TrackRow: FC<TrackRowProps> = ({ track, index, visible }): ReactNode => {
    const playback = usePlayback()
    const source = useMemo(() => trackSource(track), [track])
    const sliderRef = useRef<HTMLDivElement>(null)
    const dragging = useRef(false)
    const lastSeek = useRef(0)

    const active = playback.activeId === source.id
    const status = active ? playback.status : 'idle'
    const duration = active && playback.duration > 0 ? playback.duration : track.durationSeconds
    const position = active ? playback.position : 0
    const progress = duration > 0 ? Math.min(1, position / duration) : 0
    const playing = status === 'playing'

    const seekFromPointer = (clientX: number) => {
        const slider = sliderRef.current
        if (!slider) return

        const rect = slider.getBoundingClientRect()
        const fraction = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
        audioEngine.seek(source, fraction * duration)
    }

    const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
        dragging.current = true
        event.currentTarget.setPointerCapture(event.pointerId)
        seekFromPointer(event.clientX)
    }

    const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
        if (!dragging.current) return
        dragging.current = false
        event.currentTarget.releasePointerCapture(event.pointerId)
        seekFromPointer(event.clientX)
    }

    const onSliderKey = (event: KeyboardEvent<HTMLDivElement>) => {
        let next: number | null = null

        if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next = position + SEEK_STEP
        if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next = position - SEEK_STEP
        if (event.key === 'Home') next = 0
        if (event.key === 'End') next = Math.max(0, duration - 1)

        if (next === null) return

        event.preventDefault()
        audioEngine.seek(source, Math.min(Math.max(0, next), Math.max(0, duration - 0.5)))
    }

    const statusLine = (() => {
        if (status === 'loading') return 'Loading'
        if (status === 'blocked') return 'Tap play again to allow sound'
        if (status === 'paused') return 'Paused'
        if (status === 'playing') return 'Now playing'
        return null
    })()

    return (
        <li
            className={cn('reveal-row relative', visible && 'is-visible')}
            style={{ animationDelay: `${index * 140}ms` }}
        >
            <div
                className={cn(
                    'relative grid grid-cols-[auto_1fr_auto] items-center gap-x-4 gap-y-3 px-4 py-5 transition-colors xs:gap-x-6 xs:px-6',
                    playing ? 'bg-paper/[0.06]' : 'hover:bg-paper/[0.03]'
                )}
            >
                <span aria-hidden='true' className={cn('absolute inset-y-0 left-0 w-[5px] -skew-x-12 transition-colors', playing ? 'bg-bandana' : 'bg-paper/20')} />

                <button
                    type='button'
                    onClick={() => audioEngine.toggle(source)}
                    onPointerEnter={() => audioEngine.prepare(source)}
                    onFocus={() => audioEngine.prepare(source)}
                    aria-label={`${playing ? 'Pause' : 'Play'} ${track.title}`}
                    className={cn(
                        'flex size-14 shrink-0 items-center justify-center rounded-full transition-colors',
                        playing ? 'bg-bandana text-paper' : 'bg-paper text-ink hover:bg-night-text',
                        status === 'error' && 'bg-paper/30'
                    )}
                >
                    <PlayGlyph status={status} />
                </button>

                <div className='min-w-0'>
                    <p className='mono-label text-[11px] text-night-text'>
                        Track {track.number}
                        <span className='px-2 opacity-50'>/</span>
                        {track.theme}
                    </p>
                    <h3 lang='ms' className='display-type menu-skew mt-1 origin-left break-words text-[clamp(1.8rem,3.3vw,3.1rem)] text-paper'>
                        {track.title}
                    </h3>
                    <p className='mono-label mt-1 text-[10px] text-night-text/80'>
                        <span lang='ms'>{track.themeOriginal}</span>
                    </p>
                </div>

                <div className='flex flex-col items-end gap-2 text-night-text'>
                    <LevelBars active={playing} className='text-night-text' />
                    <span className='mono-label text-[11px] tabular-nums'>
                        {formatTime(position)} / {formatTime(duration)}
                    </span>
                </div>

                <div className='col-span-3'>
                    <div
                        ref={sliderRef}
                        role='slider'
                        tabIndex={0}
                        aria-label={`Seek in ${track.title}`}
                        aria-valuemin={0}
                        aria-valuemax={Math.round(duration)}
                        aria-valuenow={Math.round(position)}
                        aria-valuetext={`${formatTime(position)} of ${formatTime(duration)}`}
                        onPointerDown={onPointerDown}
                        onPointerMove={event => {
                            if (!dragging.current || performance.now() - lastSeek.current < 90) return
                            lastSeek.current = performance.now()
                            seekFromPointer(event.clientX)
                        }}
                        onPointerUp={onPointerUp}
                        onPointerCancel={() => {
                            dragging.current = false
                        }}
                        onKeyDown={onSliderKey}
                        className='group relative flex h-6 cursor-pointer touch-none items-center outline-offset-2'
                    >
                        <span className='relative block h-[4px] w-full bg-paper/15'>
                            <span
                                className={cn('absolute inset-0 origin-left', playing ? 'bg-bandana' : 'bg-paper/70')}
                                style={{ transform: `scaleX(${progress})` }}
                            />
                        </span>
                        <span
                            aria-hidden='true'
                            className={cn('absolute top-1/2 h-4 w-[6px] -translate-x-1/2 -translate-y-1/2 -skew-x-12 transition-opacity', active ? 'bg-paper opacity-100' : 'bg-paper opacity-0 group-hover:opacity-100')}
                            style={{ left: `${progress * 100}%` }}
                        />
                    </div>

                    <div className='mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1'>
                        <p className='mono-label text-[10px] text-night-text' aria-live='polite'>
                            {status === 'error' ? (
                                <>
                                    Preview unavailable. Hear them live on{' '}
                                    <a href={band.socialLinks.instagram} target='_blank' rel='noopener noreferrer' className='underline underline-offset-4'>
                                        Instagram
                                    </a>
                                    .
                                </>
                            ) : statusLine}
                        </p>

                        {!track.previewAudioUrl && (
                            <span className='mono-label text-[10px] text-night-text/80'>Placeholder preview, not a band recording</span>
                        )}

                        {track.externalStreamUrl && (
                            <a href={track.externalStreamUrl} target='_blank' rel='noopener noreferrer' className='mono-label text-[10px] text-paper underline underline-offset-4'>
                                Full song
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </li>
    )
}

export default TrackRow
